import { create } from "zustand";
import { STORAGE_KEYS } from "../config/constants";
import useTransactionStore from "./useTransactionStore";
import useCartStore from "./useCartStore";
import useSessionStore from "./useSessionStore";

const getTillDataFromStorage = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEYS.TILL_DATA));
  } catch {
    return null;
  }
};

const getJSONFromStorage = (key) => {
  try {
    const val = localStorage.getItem(key);
    return val ? JSON.parse(val) : null;
  } catch {
    return null;
  }
};

const useAuthStore = create((set, get) => ({
  // State — initialized as null; populated by rehydrateFromStorage() after
  // tenant storage prefix is applied (avoids reading wrong tenant's data).
  user: null,
  tokens: {
    accessToken: null,
    refreshToken: null,
  },
  isAuthenticated: false,
  tillId: null,
  tillName: null,
  storeId: null,
  tillData: null,
  userData: null,
  tillValue: null,
  username: null,

  // Actions
  setUser: (user) =>
    set({
      user,
      isAuthenticated: !!user,
    }),

  setTokens: ({ accessToken, refreshToken }) => {
    if (refreshToken) {
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    }
    set({ tokens: { accessToken, refreshToken } });
  },

  setTillData: (tillData) => {
    localStorage.setItem(STORAGE_KEYS.TILL_DATA, JSON.stringify(tillData));
    const tillId = tillData?.tillAccess?.cwrTill?.till || null;
    const storeId = tillData?.tillAccess?.csBunit?.csBunitId || null;
    set({ tillData, tillId, storeId });

    // Push config to main-process background order sync
    if (window.electronAPI?.sync?.setOrderSyncConfig && tillData?.tillAccess) {
      const ta = tillData.tillAccess;
      window.electronAPI.sync.setOrderSyncConfig({
        serverUrl: window.__CW_TENANT_URLS__?.REACT_APP_serverUrl || process.env.REACT_APP_serverUrl,
        tillId: ta.cwrTill?.cwrTillID,
        userId: ta.csUserId,
        clientId: ta.csClientId,
        bunitId: ta.csBunit?.csBunitId,
        doctypeId: ta.csBunit?.cwrCsDoctypeId,
        pricelistId: ta.csBunit?.cwrSpricelistId,
        warehouseId: ta.csBunit?.mWarehouse?.mWarehouseID,
        currencyId: ta.csBunit?.currencies?.[0]?.csCurrencyId,
        taxIncludeFlag: ta.csBunit?.isTaxIncluded || "Y",
        precision: ta.csBunit?.currencies?.[0]?.prcPrecision || 2,
        customerAddressId: ta.csBunit?.customerAddress?.sCustomerAddressID,
        b2cCustomerId: ta.csBunit?.b2cCustomer?.sCustomer?.sCustomerID,
      }).catch(() => {});
    }
  },

  setTillId: (tillId) => {
    localStorage.setItem(STORAGE_KEYS.TILL_ID, tillId);
    set({ tillId });
  },

  setTillName: (tillName) => {
    localStorage.setItem(STORAGE_KEYS.TILL_NAME, tillName);
    set({ tillName });
  },

  setUserData: (data) => {
    localStorage.setItem("userData", JSON.stringify(data));
    set({ userData: data });
  },

  setTillValue: (value) => {
    localStorage.setItem("tillValue", JSON.stringify(value));
    set({ tillValue: value });
  },

  setUsername: (name) => {
    localStorage.setItem("username", name);
    set({ username: name });
  },

  // Re-read all keys from localStorage into the store.
  // Must be called after tenant storage monkey-patch is applied on reload.
  // Validates tillData has expected shape — discards corrupt/stale data
  // so the app falls through to a fresh API fetch on the Till page.
  rehydrateFromStorage: () => {
    const tillData = getTillDataFromStorage();
    const isValid = tillData && tillData.tillAccess && tillData.tillAccess.cwrTill;

    const tillId = isValid ? (tillData.tillAccess.cwrTill.till || null) : (localStorage.getItem(STORAGE_KEYS.TILL_ID) || null);
    const tillName = localStorage.getItem(STORAGE_KEYS.TILL_NAME) || null;
    const storeId = isValid ? (tillData.tillAccess.csBunit?.csBunitId || null) : null;
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || null;

    if (tillData && !isValid) {
      console.warn("[AuthStore] Stale/corrupt tillData detected — discarding");
      localStorage.removeItem(STORAGE_KEYS.TILL_DATA);
    }

    set({
      tillData: isValid ? tillData : null,
      tillId,
      tillName,
      storeId,
      tokens: { accessToken: get().tokens.accessToken, refreshToken },
      userData: getJSONFromStorage("userData"),
      tillValue: getJSONFromStorage("tillValue"),
      username: localStorage.getItem("username") || null,
    });
  },

  // Logout clears user-specific state but preserves till binding.
  // The till is physical hardware shared by multiple cashiers — it stays
  // linked until explicitly unlinked via unlinkTill().
  logout: () => {
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.TILL_DATA);
    localStorage.removeItem(STORAGE_KEYS.TILL_ID);
    localStorage.removeItem(STORAGE_KEYS.TILL_NAME);
    localStorage.removeItem("userData");
    localStorage.removeItem("username");
    localStorage.removeItem("cartObj");
    localStorage.removeItem("exchangeCartObj");
    localStorage.removeItem("walletDataForCardIntegration");
    localStorage.removeItem("omsOrderDetails");
    localStorage.removeItem("giftCardIntegrationTopupData");
    localStorage.removeItem("callCenterHoldQueue");
    localStorage.removeItem("isPanCustomer");
    localStorage.removeItem("isNriBilling");
    localStorage.removeItem("isCreditSale");
    localStorage.removeItem("isB2bEnabled");
    useTransactionStore.getState().setIsCreditSale(null);
    useTransactionStore.getState().setIsB2bEnabled(null);
    useTransactionStore.getState().setWalletPayments([]);
    useTransactionStore.getState().setSelectedReturnType(null);
    useTransactionStore.getState().setSalesReturn("N");
    useCartStore.getState().setExchangeCart(null);
    localStorage.removeItem("customerAddressIds");
    // Clear lastUpdatedTime so the next login triggers a full sync.
    // Login.js also checks tillSession (null after logout) as a backup signal
    // in case the async SQLite write doesn't complete before page navigation.
    useSessionStore.getState().setLastUpdatedTime(null);
    // tillValue is intentionally NOT cleared — till stays linked to device
    set({
      user: null,
      tokens: { accessToken: null, refreshToken: null },
      isAuthenticated: false,
      tillId: null,
      tillName: null,
      storeId: null,
      tillData: null,
      userData: null,
      username: null,
      // tillValue preserved
    });
  },

  // Unlink till: clears everything including till binding.
  // After this, user must select a till on next login.
  unlinkTill: () => {
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.TILL_DATA);
    localStorage.removeItem(STORAGE_KEYS.TILL_ID);
    localStorage.removeItem(STORAGE_KEYS.TILL_NAME);
    localStorage.removeItem(STORAGE_KEYS.TILL_VALUE);
    localStorage.removeItem("userData");
    localStorage.removeItem("tillValue");
    localStorage.removeItem("username");
    set({
      user: null,
      tokens: { accessToken: null, refreshToken: null },
      isAuthenticated: false,
      tillId: null,
      tillName: null,
      storeId: null,
      tillData: null,
      userData: null,
      tillValue: null,
      username: null,
    });
  },

  // Selectors
  getPrecision: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.csBunit?.currencies?.[0]?.prcPrecision ?? 2;
  },

  getStoreId: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.csBunit?.csBunitId || null;
  },

  getPaymentMethods: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.csBunit?.paymentMethodList || [];
  },

  getDefaultCustomer: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.csBunit?.b2cCustomer || null;
  },

  getTillConfig: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.cwrTill || null;
  },

  getCurrencyType: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.csBunit?.currencies?.[0] || null;
  },

  getRoundingMethod: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.csBunit?.currencies?.[0]?.roundingMethod || "NR";
  },

  getTaxIncludeFlag: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.csBunit?.isTaxIncluded || "Y";
  },

  getTillAccess: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess || null;
  },

  getUserAccessController: () => {
    const tillData = get().tillData;
    try {
      return JSON.parse(tillData?.tillAccess?.userAccessController || "{}");
    } catch {
      return {};
    }
  },

  getPrintTemplate: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.cwrTill?.printTemplate || null;
  },

  getHardwareController: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.cwrTill?.hardwareController || null;
  },

  getBusinessUnit: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.csBunit || null;
  },
}));

export default useAuthStore;
